import { z } from 'zod';
import {
  TREND_KINDS,
  TREND_TAGS,
  trendDefinitionSchema,
  trendSectionSchema,
  type Economics,
  type ModelCosts,
  type PublishIssue,
  type TrendDefinition,
  type TrendEconomics,
  type TrendKind,
  type TrendStatus,
} from './trends.js';

// Типы API админки (/admin/*). Деньги — целые ₽, время — ISO-строки.

export const ADMIN_MUTATION_HEADER = 'x-trendator-admin';

export interface AdminMe {
  telegramId: string;
  name: string;
}

export interface AdminAuthConfig {
  /** Имя бота для Telegram Login Widget; null — вход виджетом не настроен. */
  botUsername: string | null;
  devLogin: boolean;
}

/** Данные Telegram Login Widget. */
export const telegramLoginSchema = z.object({
  id: z.coerce.number().int().positive(),
  first_name: z.string().max(256),
  last_name: z.string().max(256).optional(),
  username: z.string().max(64).optional(),
  photo_url: z.string().max(1024).optional(),
  auth_date: z.coerce.number().int().positive(),
  hash: z.string().regex(/^[0-9a-f]{64}$/),
});
export type TelegramLogin = z.infer<typeof telegramLoginSchema>;

// ---------- Тренды ----------

export const adminTrendInputSchema = z.object({
  slug: z.string().regex(/^[a-z0-9][a-z0-9-]{1,47}$/, 'Латиница, цифры и дефис, 2–48 символов'),
  title: z.string().trim().min(1).max(60),
  subtitle: z.string().trim().max(40).default(''),
  description: z.string().trim().max(500).default(''),
  kind: z.enum(TREND_KINDS).default('photo'),
  section: trendSectionSchema.default('self'),
  tags: z.array(z.enum(TREND_TAGS)).max(2).default([]),
  price: z.number().int().min(0).max(100_000),
  allowFree: z.boolean().default(false),
  sortOrder: z.number().int().min(0).max(10_000).default(100),
  coverGradient: z.string().max(300).regex(/^[a-z0-9#(),.%\s-]+$/i, 'Только CSS-градиент').optional(),
  definition: trendDefinitionSchema,
});
export type AdminTrendInput = z.input<typeof adminTrendInputSchema>;
export const adminTrendPatchSchema = adminTrendInputSchema.partial();

export interface AdminTrendListItem {
  id: string;
  slug: string;
  title: string;
  kind: TrendKind;
  section: string;
  status: TrendStatus;
  dirty: boolean;
  price: number;
  publishedPrice: number | null;
  coverUrl: string | null;
  coverGradient: string;
  frames: number;
  generations7d: number;
  updatedAt: string;
}

export interface AdminTestRun {
  generationId: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  draft: boolean;
  createdAt: string;
  durationSec: number | null;
  framesDone: number;
  frames: number;
}

export interface AdminTrendDetail {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  kind: TrendKind;
  section: string;
  tags: string[];
  status: TrendStatus;
  dirty: boolean;
  price: number;
  allowFree: boolean;
  sortOrder: number;
  coverGradient: string;
  coverUrl: string | null;
  beforeUrl: string | null;
  definition: TrendDefinition;
  published: { version: number; price: number; createdAt: string } | null;
  economics: TrendEconomics;
  issues: PublishIssue[];
  lastTest: AdminTestRun | null;
  updatedAt: string;
}

export const adminTestRunSchema = z.object({
  photoIds: z.array(z.string().uuid()).max(4).default([]),
  /** Взять двойника админа вместо фото. */
  useTwin: z.boolean().default(false),
  fields: z.record(z.string().max(20), z.string().max(300)).default({}),
  /** draft — текущая цепочка черновика, published — опубликованная версия. */
  use: z.enum(['draft', 'published']).default('draft'),
});

// ---------- Генерации ----------

export type AdminGenStatus = 'queued' | 'running' | 'stuck' | 'succeeded' | 'failed';

export interface AdminUserRef {
  id: number;
  telegramId: string;
  username: string | null;
  firstName: string;
}

export interface AdminGenerationRow {
  id: string;
  user: AdminUserRef;
  title: string;
  source: 'manual' | 'trend';
  kindLabel: string;
  providers: string[];
  status: AdminGenStatus;
  price: number;
  free: boolean;
  test: boolean;
  refunded: boolean;
  createdAt: string;
}

export interface AdminTask {
  id: string;
  step: number;
  index: number;
  kind: 'image' | 'video' | 'song' | 'cutout';
  label: string | null;
  /** Перегенерации брака за наш счёт и проверки качества по попыткам. */
  regenerations: number;
  qc: { at: string; face: number | null; vision: { ok: boolean; reason: string | null } | null; ok: boolean; reason: string | null }[];
  status: 'queued' | 'submitted' | 'succeeded' | 'failed';
  provider: string | null;
  model: string | null;
  providerTaskId: string | null;
  previous: { provider?: string; taskId?: string; error?: string } | null;
  attempts: number;
  error: string | null;
  costUsd: number | null;
  createdAt: string;
  submittedAt: string | null;
  finishedAt: string | null;
}

export interface AdminGenerationDetail extends AdminGenerationRow {
  user: AdminUserRef;
  trendId: string | null;
  trendSlug: string | null;
  mode: string | null;
  currentStep: number;
  totalSteps: number;
  error: string | null;
  hold: number | null;
  released: number | null;
  costRub: number;
  costUsd: number;
  tasks: AdminTask[];
  startedAt: string | null;
  finishedAt: string | null;
  /** Результаты видны сразу только у тест-прогонов; у юзеров — по кнопке с записью в аудит. */
  resultsHidden: boolean;
  results: { url: string; label: string | null; type?: 'image' | 'video' | 'audio'; coverUrl?: string | null; title?: string | null }[];
  kind: 'photo' | 'video' | 'song' | 'stickers';
  twin: boolean;
  fieldValues: Record<string, string> | null;
  /** Дуо: друг, давший согласие. */
  friend: AdminUserRef | null;
  /** Стикерпак: имя набора или причина, почему не собрался. */
  stickerSet: string | null;
  stickerError: string | null;
}

// ---------- Юзеры ----------

export interface AdminUserRow extends AdminUserRef {
  createdAt: string;
  balance: number;
  paid: number;
  generations: number;
  banned: boolean;
  source: string;
}

export interface AdminLedgerRow {
  id: number;
  type: string;
  amount: number;
  balanceAfter: number;
  comment: string | null;
  createdAt: string;
}

export interface AdminUserCard extends AdminUserRow {
  lastName: string | null;
  languageCode: string | null;
  isPremium: boolean;
  refCode: string;
  refSubId: string | null;
  referrer: { id: number; username: string | null; firstName: string } | null;
  consentAt: string | null;
  freeGenerationUsed: boolean;
  bannedAt: string | null;
  banReason: string | null;
  lastSeenAt: string;
  toppedUp: number;
  spent: number;
  referrals: { id: number; username: string | null; firstName: string; createdAt: string; paid: number }[];
  ledger: AdminLedgerRow[];
  uploads: number;
  twin: {
    status: 'photos' | 'liveness' | 'ready';
    photos: number;
    okPhotos: number;
    consistency: number | null;
    liveness: { front: boolean; left: boolean; right: boolean };
    readyAt: string | null;
  } | null;
  /** Партнёрка: счёт, холд, доступно, заявки, постбэк. */
  partner: {
    balance: number;
    held: number;
    available: number;
    invited: number;
    bought: number;
    earned: number;
    postbackUrl: string | null;
    payouts: AdminPayout[];
  };
  /** Нарушения: подтверждённые за 30 дней, открытые флаги, автоблоки за сутки. */
  moderation: { confirmed30d: number; open: number; blocked24h: number };
}

export const adminAdjustSchema = z.object({
  amount: z.number().int().refine((n) => n !== 0, 'Сумма не может быть 0').refine((n) => Math.abs(n) <= 100_000, 'Слишком много'),
  reason: z.string().trim().min(3, 'Причина обязательна').max(200),
  /** Ключ формы: повторный клик не задвоит запись. */
  key: z.string().uuid(),
});

export const adminBanSchema = z.object({
  banned: z.boolean(),
  reason: z.string().trim().max(200).default(''),
});

export const adminMessageSchema = z.object({ text: z.string().trim().min(1).max(3000) });

// ---------- Платежи ----------

export interface AdminPaymentRow {
  id: string;
  user: AdminUserRef;
  provider: string;
  method: string;
  packageId: string;
  amount: number;
  bonus: number;
  status: string;
  stuck: boolean;
  test: boolean;
  providerPaymentId: string | null;
  createdAt: string;
  paidAt: string | null;
}

export interface AdminWebhookRow {
  id: number;
  provider: string;
  idempotencyKey: string;
  receivedAt: string;
  payload: unknown;
}

// ---------- Провайдеры и настройки ----------

export interface AdminProviderSettings {
  order: ('kie' | 'fal')[];
  enabled: Record<'kie' | 'fal', boolean>;
}

export interface AdminProvidersView {
  settings: AdminProviderSettings;
  /** Контроль качества: выключатель и доступность сервиса лиц у API. */
  qc: { enabled: boolean };
  faceService: boolean;
  configured: Record<'kie' | 'fal', boolean>;
  modelCosts: ModelCosts;
  economics: Economics;
  kieCredits: number | null;
  stats: { provider: string; submitted: number; succeeded: number; failed: number; fellBack: number; avgSec: number | null; costUsd: number }[];
  errors: { taskId: string; generationId: string; provider: string | null; error: string; createdAt: string }[];
}

export const adminProviderSettingsSchema = z.object({
  order: z.array(z.enum(['kie', 'fal'])).length(2).refine((a) => new Set(a).size === 2, 'Без повторов'),
  enabled: z.object({ kie: z.boolean(), fal: z.boolean() }),
});
export const adminQcSettingsSchema = z.object({ enabled: z.boolean() });
export const adminEconomicsSchema = z.object({
  usdRub: z.number().positive().max(1000),
  acquiringPct: z.number().min(0).max(50),
  defectReservePct: z.number().min(0).max(100),
});
const usd = z.number().min(0).max(100);
export const adminModelCostsSchema = z.record(
  z.string().min(1).max(64),
  z.object({
    kie: z.record(z.string().max(10), usd).optional(),
    fal: z.record(z.string().max(10), usd).optional(),
  }),
);

// ---------- Дашборд ----------

export type AdminPeriod = 'today' | '7d' | '30d';

export interface AdminDashboard {
  period: AdminPeriod;
  health: {
    kie: { configured: boolean; enabled: boolean; credits: number | null };
    fal: { configured: boolean; enabled: boolean };
    payments: { provider: string; test: boolean }[];
    queues: { photo: number; video: number; check: number } | null;
    stuckGenerations: number;
    stuckPayments: number;
  };
  kpi: {
    revenue: number;
    revenuePrev: number;
    costRub: number;
    acquiringRub: number;
    marginRub: number;
    marginPct: number;
    generations: number;
    paidGenerations: number;
    freeGenerations: number;
    failedGenerations: number;
    newUsers: number;
    payingUsers: number;
  };
  days: { day: string; revenue: number; costRub: number }[];
  funnel: { started: number; consent: number; generated: number; paid: number };
  trends: { id: string; title: string; kind: string; coverGradient: string; generations: number; revenue: number; marginRub: number; failedPct: number }[];
  quality: { failedPct: number; refundPct: number; medianSec: number | null };
  alerts: { level: 'warn' | 'error'; title: string; text: string; href?: string }[];
}

export interface AdminAuditRow {
  id: number;
  actorName: string;
  actorTelegramId: string;
  action: string;
  entityType: string;
  entityId: string | null;
  payload: unknown;
  createdAt: string;
}

export const TREND_STATUS_LABELS: Record<TrendStatus, string> = {
  draft: 'черновик',
  published: 'опубликован',
  disabled: 'выключен',
};

// ---------- Партнёрка ----------

export interface AdminPayout {
  id: string;
  user: AdminUserRef;
  method: 'sbp' | 'usdt';
  amount: number;
  /** Полные реквизиты (видит только админ). */
  details: { phone?: string; bank?: string; address?: string };
  status: 'pending' | 'paid' | 'rejected';
  note: string | null;
  decidedBy: string | null;
  createdAt: string;
  decidedAt: string | null;
}

export interface AdminPartnerEntry {
  id: number;
  partner: { id: number; username: string | null; firstName: string };
  type: string;
  amount: number;
  percent: number | null;
  referralUserId: number | null;
  availableAt: string | null;
  comment: string | null;
  createdAt: string;
}

export interface AdminPartnerView {
  totals: { accrued: number; held: number; onAccounts: number; pendingPayouts: number; pendingAmount: number; paidOut: number };
  payouts: AdminPayout[];
  entries: AdminPartnerEntry[];
}

export const adminPayoutDecisionSchema = z.object({ note: z.string().trim().max(200).optional() });

// ---------- Модерация ----------

export interface AdminFlag {
  id: number;
  user: { id: number; telegramId: string; username: string | null; firstName: string };
  userBanned: boolean;
  /** Подтверждённых нарушений юзера за 30 дней. */
  strikes: number;
  source: 'prompt' | 'input' | 'output' | 'report';
  action: 'flag' | 'block' | 'report';
  categories: string[];
  reason: string | null;
  text: string | null;
  generationId: string | null;
  /** Сколько файлов можно посмотреть (сами ссылки — только через reveal, с записью в аудит). */
  media: number;
  reporterId: number | null;
  reportReason: string | null;
  status: 'open' | 'confirmed' | 'dismissed';
  note: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
}

export interface AdminModerationView {
  counts: { open: number; blocked24h: number; reports: number };
  enabled: boolean;
  /** Есть ли модель (QC_API_KEY / KIE_API_KEY): без неё работает только стоп-лист. */
  modelConfigured: boolean;
  items: AdminFlag[];
}

export const adminFlagDecisionSchema = z.object({
  decision: z.enum(['confirmed', 'dismissed']),
  note: z.string().trim().max(500).optional(),
  /** Только при подтверждении. */
  ban: z.boolean().default(false),
  deleteFiles: z.boolean().default(false),
});
export type AdminFlagDecision = z.input<typeof adminFlagDecisionSchema>;

export const adminModerationSettingsSchema = z.object({ enabled: z.boolean() });
